import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const textClass = scrolled
    ? 'text-foreground-950'
    : 'text-background-50';
  const navBgClass = scrolled
    ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
    : 'bg-transparent';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <Link
          to="/"
          className={`font-heading text-xl md:text-2xl tracking-tight whitespace-nowrap transition-colors duration-300 ${textClass}`}
        >
          Hipobuy<span className={scrolled ? 'text-primary-500' : 'text-primary-300'}>Spreadsheet</span>
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-3">
          {/* Home */}
          <Link
            to="/"
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                : 'text-background-200 hover:text-background-50 hover:bg-white/10'
            }`}
          >
            <i className="ri-home-4-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            Accueil
          </Link>

          {/* Shopping Guide */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                : 'text-background-200 hover:text-background-50 hover:bg-white/10'
            }`}
          >
            <i className="ri-shopping-bag-3-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
            Guide d&apos;achat
          </a>

          {/* Category Dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'bg-background-100 border-background-200 text-foreground-700 hover:bg-background-200'
                  : 'bg-white/10 border-white/20 text-background-50 hover:bg-white/20'
              }`}
            >
              <i className="ri-grid-line w-4 h-4 flex items-center justify-center text-sm"></i>
              <span>Catégories</span>
              <i
                className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center text-sm transition-transform duration-200 ${
                  dropdownOpen ? 'rotate-180' : ''
                }`}
              ></i>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-background-50 border border-background-200 rounded-xl shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                    onClick={() => setDropdownOpen(false)}
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* CTA */}
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              scrolled
                ? 'bg-primary-500 text-background-50 hover:bg-primary-600'
                : 'bg-background-50 text-foreground-900 hover:bg-background-100'
            }`}
          >
            Acheter maintenant
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center text-sm"></i>
          </a>
        </div>

        {/* Mobile hamburger */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${textClass}`}
          aria-label="Menu"
        >
          <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} w-6 h-6 flex items-center justify-center text-xl`}></i>
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200 shadow-lg">
          <div className="px-4 py-3 space-y-1">
            <Link
              to="/"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-primary-50 text-sm font-medium cursor-pointer"
            >
              <i className="ri-home-4-line w-5 h-5 flex items-center justify-center"></i>
              Accueil
            </Link>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-primary-50 text-sm font-medium cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line w-5 h-5 flex items-center justify-center"></i>
              Guide d&apos;achat
            </a>
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full px-3 py-2.5 rounded-lg text-foreground-700 hover:bg-primary-50 text-sm font-medium cursor-pointer"
            >
              <span className="flex items-center gap-2">
                <i className="ri-grid-line w-5 h-5 flex items-center justify-center"></i>
                Catégories
              </span>
              <i className={`ri-arrow-down-s-line w-5 h-5 flex items-center justify-center transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileCatOpen && (
              <div className="pl-8 space-y-0.5 border-l-2 border-background-200 ml-3">
                {categories.map((cat) => (
                  <a
                    key={cat.id}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="block px-3 py-2 text-sm text-foreground-600 hover:text-primary-600 cursor-pointer whitespace-nowrap"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            )}
            <div className="pt-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer whitespace-nowrap"
              >
                Acheter maintenant
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}